import getEnv from '@/utils/env.js'
import MwCompatibilityCheckerPlugin from '@highq/mw-compatibility-checker'
import FontAwesomeIcon from '@plugins/font-awesome.js'
import { initI18n } from '@plugins/i18n'
import StorageManipulationPlugin, { setRouter, useStorageManipulation } from '@plugins/storage-manipulation'
import toast, { ToastOptions } from '@plugins/toast/index.js'
import axios from 'axios'
import { createApp } from 'vue'
import { createStore } from 'vuex'
import packageJson from '../package.json'
import App from './App.vue'
import router from './router.js'
import MiddlewareConnectorPlugin from '@plugins/middleware-connector'

const storageManipulation = useStorageManipulation()
const vueApp = createApp(App)

vueApp.use(MiddlewareConnectorPlugin, { middlewareUrl: getEnv('VITE_MIDDLEWARE_URL') })

vueApp.use(MwCompatibilityCheckerPlugin, {
    versionEndpointUrl: getEnv('VITE_MIDDLEWARE_URL') + '/version',
    compatibleSemverVersion: packageJson.compatibleMiddlewareVersion
})

// setup vuex store
const store = new createStore({
    state() {
        return {
            statistics: null,
            clientdata: null,
            clientlogo: null,
            corporations: [],
            editcorporation: null,
            editcorporationlogo: null,
            editcontact: null,
            salutations: [],
            titles: [],
            roles: [],
            editchangeoverday: null,
            editattestation: null,
            editlink: null,
            edittext: null,
            editproduct: null,
            editmailtemplate: null,
            listview: false,
            addonproductconfigurations: [],
        }
    },
    mutations: {
        newStatistics(state, statistics) {
            state.statistics = statistics
        },
        newClientData(state, clientdata) {
            state.clientdata = clientdata
        },
        newClientLogo(state, clientlogo) {
            state.clientlogo = clientlogo
        },
        newCorporations(state, corporations) {
            state.corporations = corporations
        },
        newEditCorporation(state, corporation) {
            state.editcorporation = corporation
        },
        newEditCorporationLogo(state, logo) {
            state.editcorporationlogo = logo
        },
        newEditContact(state, contact) {
            state.editcontact = contact
        },
        newSalutations(state, salutationlist) {
            state.salutations = salutationlist
        },
        newTitles(state, titlelist) {
            state.titles = titlelist
        },
        newRoles(state, rolelist) {
            state.roles = rolelist
        },
        newEditChangeoverDay(state, changeoverday) {
            state.editchangeoverday = changeoverday
        },
        newEditAttestation(state, attestation) {
            state.editattestation = attestation
        },
        newEditLink(state, link) {
            state.editlink = link
        },
        newEditText(state, text) {
            state.edittext = text
        },
        newEditProduct(state, product) {
            state.editproduct = product
        },
        newEditAddonProductConfigurations(state, productconfigurations) {
            state.addonproductconfigurations = productconfigurations
        },
        newEditMailTemplate(state, template) {
            state.editmailtemplate = template
        },
        switchListView(state, listview) {
            state.listview = listview
        }
    }
})

// register and setup toast component
const toastOptions = new ToastOptions({ position: ToastOptions.POSITION.BOTTOM_MID })
vueApp.use(toast, toastOptions)

vueApp.component('FaIcon', FontAwesomeIcon)

// register storage helper
vueApp.use(router)
// to avoid a circular ref, the router
// is manually injected in to the storage plugin
setRouter(router)
vueApp.use(StorageManipulationPlugin)
vueApp.use(initI18n())
vueApp.use(store)

// configure axios
axios.defaults.timeout = 20000 // default timeout
const HEADER_HQ_TOKEN = 'hq-token'
const HEADER_JWT_TOKEN = 'hq-jwt-token'

axios.interceptors.request.use(config => {
    // set sessionid as header in every request
    // if one is present
    const sessionData = storageManipulation.getSessionData()

    if (sessionData && sessionData.token) {
        config.headers[HEADER_HQ_TOKEN]   = sessionData.soapSessionId
        config.headers[HEADER_JWT_TOKEN]  = sessionData.token
    }

    return config
})

// interceptors for to session
axios.interceptors.response.use(response => {
    // if the session from the response equals the current TO session
    // we made a successfull request to the tows and can update the
    // creation time of the sessionData so that the session will not end
    const sessionData = storageManipulation.getSessionData()

    if (response.data.status && sessionData) {
        if (parseInt(response.data.status.ErrorCode) === 3) {
            // ErrorCode 3 --> session has expired
            router.push({ name: 'login' })
        }

        const requestToken = response.data.status.SessionID
        const currentToken = sessionData.token

        if (requestToken == currentToken) {
            storageManipulation.extendSession()
        }
    }

    return response
})

vueApp.mount('#app')